import { useQuery } from '@tanstack/react-query'

import { TeamsApi } from '@/services/users/teamsApi'
import { AdvertisersApi } from '@/services/advertisers/advertisersApi'
import { useCurrentUser } from '../contexts/UserContext'
import { useNetwork } from '../contexts/NetworkContext'

export interface DefaultCurrencyOwners {
  advertiserId?: string
  teamId?: string
}

/**
 * Resolves the currency an object should default to, using the same fallback
 * chain as the legacy app (first non-empty wins):
 *   1. Advertiser's defaultCurrency
 *   2. Owning team's defaultCurrency
 *   3. User's preferredCurrency
 *   4. Network's defaultCurrency
 */
export function useDefaultCurrency({ advertiserId, teamId }: DefaultCurrencyOwners): string {
  const { currentNetwork } = useNetwork()
  const currentUser = useCurrentUser()

  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
    enabled: !!teamId,
  })
  const { data: advertisers = [] } = useQuery({
    queryKey: ['advertisers'],
    queryFn: () => AdvertisersApi.getAll(),
    enabled: !!advertiserId,
  })

  return (
    advertisers.find((a) => a.id === advertiserId)?.defaultCurrency ||
    teams.find((t) => t.id === teamId)?.defaultCurrency ||
    currentUser?.preferredCurrency ||
    currentNetwork?.defaultCurrency ||
    'USD'
  )
}
